import { useRef, useState } from "react";
import { trpc } from "../../_core/trpc";
import { Panel } from "./Admin";
import { LIMITS, useUploader } from "./upload";
import { UploadStatusBar } from "./UploadStatus";
import { MusicPlayer } from "../../components/lux/MusicPlayer";
function AdminMusic() {
  const list = trpc.admin.listMedia.useQuery({ kind: "audio" });
  const update = trpc.admin.updateMedia.useMutation();
  const remove = trpc.admin.deleteMedia.useMutation();
  const settings = trpc.admin.getSettings.useQuery();
  const save = trpc.admin.saveSettings.useMutation();
  const utils = trpc.useUtils();
  const { uploadOne, status, reset, busy } = useUploader("audio");
  const fileRef = useRef(null);
  const [caption, setCaption] = useState("");
  const [errors, setErrors] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const tracks = list.data ?? [];
  const refresh = async () => {
    await utils.admin.listMedia.invalidate();
    await utils.admin.overview.invalidate();
    await utils.public.state.invalidate();
  };
  const onFile = async (files) => {
    if (!files || files.length === 0) return;
    setErrors([]);
    for (const f of Array.from(files)) {
      const res = await uploadOne(f, { caption: caption.trim() || void 0 });
      if (!res.ok && res.message) setErrors((e) => [...e, `${f.name}: ${res.message}`]);
    }
    setCaption("");
    await refresh();
    if (fileRef.current) fileRef.current.value = "";
    reset();
  };
  return <Panel
    eyebrow="Music"
    title="The song"
    description={`${LIMITS.audio.label}.`}
  >
      {
    /* ── availability rule ── */
  }
      <div className="glass rounded-xl p-5">
        <p className="eyebrow mb-1">When can she play it?</p>
        <p className="mb-4 text-[0.78rem] leading-relaxed text-[color:var(--muted-foreground)]">
          Playback never starts on its own — she always presses play. This only controls whether the
          player is offered before midnight.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {[
    [true, "Only after the reveal", "The countdown stays silent; the song appears with her birthday experience."],
    [false, "Any time, including before midnight", "The player is also offered on the teaser page."]
  ].map(([isAfterUnlock, label, hint]) => {
    const active = settings.data ? settings.data.revealMusicAfterUnlock === isAfterUnlock : false;
    return <button
      key={label}
      type="button"
      onClick={async () => {
        await save.mutateAsync({ revealMusicAfterUnlock: isAfterUnlock });
        await utils.admin.getSettings.invalidate();
        await utils.public.state.invalidate();
      }}
      className={`rounded-xl border p-4 text-left transition ${active ? "border-[rgba(233,168,242,.5)] bg-[rgba(233,168,242,.1)]" : "border-[rgba(233,168,242,.16)] hover:border-[rgba(233,168,242,.32)]"}`}
      aria-pressed={active}
    >
                <span className="flex items-center gap-2">
                  <span
      aria-hidden="true"
      className={`grid h-4 w-4 place-items-center rounded-full border ${active ? "border-[#e9a8f2] bg-[#e9a8f2]" : "border-white/30"}`}
    >
                    {active && <span className="h-1.5 w-1.5 rounded-full bg-[#1a0b24]" />}
                  </span>
                  <span className="text-sm text-[#f3f4f9]">{label}</span>
                </span>
                <span className="mt-1.5 block pl-6 text-[0.72rem] leading-relaxed text-[color:var(--muted-foreground)]">
                  {hint}
                </span>
              </button>;
  })}
        </div>
      </div>

      {
    /* ── upload ── */
  }
      <div className="glass mt-4 rounded-xl p-5">
        <div className="flex flex-col items-center gap-3 py-3 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full border border-[rgba(233,168,242,.3)]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 18V6l10-2v12M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM19 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" stroke="#e9a8f2" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <p className="font-display text-lg text-[#f3f4f9]">Upload her song</p>
          <input
    className="field-lux max-w-sm"
    placeholder="Track title (optional)"
    value={caption}
    onChange={(e) => setCaption(e.target.value)}
    aria-label="Track title"
  />
          <button
    type="button"
    onClick={() => fileRef.current?.click()}
    className="btn-gold !min-h-[42px] !text-[0.66rem]"
    disabled={busy}
  >
            {busy ? "Uploading\u2026" : "Choose audio"}
          </button>
          <input
    ref={fileRef}
    type="file"
    accept={LIMITS.audio.types.join(",")}
    multiple
    className="hidden"
    onChange={(e) => void onFile(e.target.files)}
  />
        </div>

        <div className="space-y-2">
          <UploadStatusBar status={status} />
          {errors.map((e, i) => <p key={i} role="alert" className="text-[0.75rem] text-[#ffc9cd]">
              {e}
            </p>)}
        </div>

        <p className="mt-3 rounded-lg border border-[rgba(233,168,242,.16)] bg-white/[0.02] px-3 py-2 text-[0.7rem] leading-relaxed text-[color:var(--muted-foreground)]">
          <strong className="text-[#dcdff0]">Use music you have the right to share.</strong> This site
          plays the file you upload; it does not connect to Spotify or Apple Music. A track bought or
          downloaded from a licensed source is the safe choice.
        </p>
      </div>

      {
    /* ── preview through the real player ── */
  }
      {tracks.length > 0 && <div className="mt-4">
          <p className="eyebrow mb-2">Preview — exactly what she will use</p>
          <MusicPlayer
    tracks={tracks.map((t) => ({
      id: t.id,
      url: t.url,
      caption: t.caption,
      mime: t.mime,
      durationSec: t.durationSec
    }))}
    title="Her song"
  />
        </div>}

      {
    /* ── list ── */
  }
      <div className="mt-5 space-y-3">
        {list.isLoading && <p className="text-sm text-[color:var(--muted-foreground)]">Loading tracks…</p>}

        {!list.isLoading && tracks.length === 0 && <div className="glass rounded-xl px-6 py-14 text-center">
            <p className="font-display text-lg text-[#f3f4f9]">No music yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-[color:var(--muted-foreground)]">
              Upload the birthday song above. Without it the page simply has no player — everything
              else still works.
            </p>
          </div>}

        {tracks.map((t) => <div key={t.id} className="glass flex flex-wrap items-center gap-3 rounded-xl p-4">
            <span className="pill">{t.mime ?? "audio"}</span>
            <span className="pill">{(t.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>
            {t.durationSec ? <span className="pill">
                {Math.floor(t.durationSec / 60)}:{String(t.durationSec % 60).padStart(2, "0")}
              </span> : null}
            {t.visibleBeforeUnlock && <span className="pill pill-locked">before midnight</span>}

            <input data-testid="track-title"
    className="field-lux min-w-[180px] flex-1 !min-h-[36px] !text-sm"
    defaultValue={t.caption ?? ""}
    placeholder="Track title"
    aria-label="Track title"
    onBlur={async (e) => {
      const v = e.target.value.trim();
      if (v !== (t.caption ?? "")) {
        await update.mutateAsync({ id: t.id, caption: v || null });
        await refresh();
      }
    }}
  />

            {confirmId === t.id ? <div className="flex gap-1">
                <button
    type="button"
    onClick={async () => {
      await remove.mutateAsync({ id: t.id });
      setConfirmId(null);
      await refresh();
    }}
    className="rounded-full bg-[#e11d48] px-3 py-1.5 text-[0.62rem] uppercase tracking-wider text-white"
  >
                  Delete
                </button>
                <button
    type="button"
    onClick={() => setConfirmId(null)}
    className="rounded-full border border-white/15 px-3 py-1.5 text-[0.62rem] uppercase tracking-wider text-[color:var(--muted-foreground)]"
  >
                  Keep
                </button>
              </div> : <button
    type="button"
    onClick={() => setConfirmId(t.id)}
    className="btn-ghost-lux !min-h-[34px] !px-3 !text-[0.6rem]"
  >
                Delete
              </button>}
          </div>)}
      </div>
    </Panel>;
}
export {
  AdminMusic as default
};
